import React, {useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {Carousel} from '@/components/ui/carousel'
import {Loader} from '@/components/loader'
import GoalQuestionnaireContent from './goalQuestionnaireContent'
import GoalBenefitsTable from './goalBenefitsTable'
import {useFetchEntries} from 'hec/hooks/useFetchEntries'
import useMediaQuery from 'hec/hooks/useMediaQuery'
import builderConfig from 'hec/builder/model-map'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getErrorMessage} from './utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isEditing} from '@builder.io/sdk-react'
import GoalResult from './goalResult'
import {useSelectedGoalData} from './useSelectedGoalData'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'

const GoalQuestionnaire = (props) => {
    const {attributes, firstQuestion, disclaimerText, benefitsTableBgImage} = props
    const {className, ...restAttributes} = attributes
    const config = getConfig()
    const isMobile = useMediaQuery('sm')

    const [questions, setQuestions] = useState([])

    const {goal, setGoal} = useSelectedGoalData()

    const {data: customer, isError: isCustomerError, error: customerError} = useCurrentCustomer()

    const shouldQueryFirstQuestion = !firstQuestion?.value?.data

    const {
        data: firstQuestionData,
        isLoading: isFirstQuestionrDataLoading,
        error: firstQuestionDataError
    } = useFetchBuilderReference({
        model: firstQuestion?.model,
        shouldQuery: shouldQueryFirstQuestion,
        referenceId: firstQuestion?.id
    })

    const {
        data: benefitsData,
        isLoading: isBenefitsDataLoading,
        error: benefitsDataError
    } = useFetchEntries({
        queryKey: ['goal-benefits'],
        options: {
            model: builderConfig.learningCenterGoalBenefit,
            apiKey: config.app.builder.api
        }
    })

    const {
        data: goalsData,
        isLoading: isGoalsDataLoading,
        isError: isGoalsDataError
    } = useFetchEntries({
        queryKey: ['goals'],
        options: {
            model: builderConfig.learningCenterGoal,
            apiKey: config.app.builder.api
        }
    })

    const addQuestion = useCallback((newQuestion) => {
        setQuestions((prevQuestions) => {
            if (!prevQuestions.some((q) => q.question === newQuestion.question)) {
                return [...prevQuestions, newQuestion]
            }
            return prevQuestions
        })
    }, [])

    if (
        isBenefitsDataLoading ||
        isGoalsDataLoading ||
        (isFirstQuestionrDataLoading && shouldQueryFirstQuestion)
    ) {
        return (
            <div className="w-full my-20 flex justify-center">
                <Loader className="w-10 h-10" data-testid="goalQuestionnaire-Loader" />
            </div>
        )
    }
    const error = isGoalsDataError || benefitsDataError || isCustomerError || firstQuestionDataError

    const errors = {
        firstQuestion: firstQuestionDataError,
        goal: isGoalsDataError,
        benefits: benefitsDataError,
        customer: customerError
    }

    const errorMessage = getErrorMessage(errors)

    if (error) {
        throw new Error(errorMessage)
    }

    const firstQuestionContent = shouldQueryFirstQuestion
        ? firstQuestionData?.data
        : firstQuestion?.value?.data

    if (firstQuestionContent && questions.length < 1) {
        addQuestion(firstQuestionContent)
    }
    const isGuest = customer.isGuest && !isEditing()

    return (
        <div className={className} {...restAttributes}>
            <Carousel
                opts={{watchDrag: false}}
                className="h-auto"
                data-testid="goalQuestionnaire-Carousel"
            >
                <GoalQuestionnaireContent
                    questions={questions}
                    setQuestions={setQuestions}
                    addQuestion={addQuestion}
                    goal={goal}
                    setGoal={setGoal}
                    isMobile={isMobile}
                    isGuest={isGuest}
                    data-testid="goalQuestionnaire-GoalQuestionnaireContent"
                />
            </Carousel>
            {isMobile && goal && (
                <GoalResult
                    goal={goal}
                    isMobile={isMobile}
                    isGuest={isGuest}
                    data-testid="goalQuestionnaire-GoalResult"
                />
            )}
            <GoalBenefitsTable
                currentGoal={goal}
                disclaimerText={disclaimerText}
                benefits={benefitsData}
                goals={goalsData}
                bgImage={benefitsTableBgImage}
                isMobile={isMobile}
                data-testid="goalQuestionnaire-GoalBenefitsTable"
            />
        </div>
    )
}

export default GoalQuestionnaire

GoalQuestionnaire.propTypes = {
    attributes: PropTypes.object,
    firstQuestion: PropTypes.object.isRequired,
    benefitsTableBgImage: PropTypes.string,
    disclaimerText: PropTypes.array
}

export const GoalQuestionnaireDefinition = {
    component: GoalQuestionnaire,
    name: 'GoalQuestionnaire',
    noWrap: true,
    inputs: [
        {
            name: 'firstQuestion',
            type: 'reference',
            required: true,
            helperText: 'Starting question for the quiz.',
            model: builderConfig.learningCenterQuestionnaireQuestion
        },

        {
            name: 'disclaimerText',
            type: 'list',
            subFields: [
                {
                    name: 'text',
                    type: 'longText',
                    required: true
                }
            ]
        },
        {
            name: 'benefitsTableBgImage',
            friendlyName: 'Benefits Table Background Image',
            type: 'file'
        }
    ]
}
